import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { QueryKeys } from 'src/constants/QueryKeys'
import { useConnectOnboard } from 'src/hooks/useAffiliate'
import { AffiliateMe } from 'src/types/Affiliate'
import { formatCents } from 'src/utils/affiliateFormat'
import { notify } from 'src/utils/notify'
import { errorMessage } from './labels'
import { ActionButton, Banner, Row, Select, SecondaryButton } from './ui'

// Countries Stripe Express can pay out to from a US platform. The backend accepts any ISO code; this list
// just keeps the picker short. Non-US accounts are set up as cross-border "recipient" accounts.
const COUNTRIES: [string, string][] = [
    ['US', 'United States'],
    ['CA', 'Canada'],
    ['GB', 'United Kingdom'],
    ['AU', 'Australia'],
    ['NZ', 'New Zealand'],
    ['IE', 'Ireland'],
    ['DE', 'Germany'],
    ['FR', 'France'],
    ['NL', 'Netherlands'],
    ['ES', 'Spain'],
    ['IT', 'Italy'],
    ['SE', 'Sweden'],
]

const COUNTRY_KEY = 'ss_connect_country'

const savedCountry = () => {
    try {
        return window.sessionStorage.getItem(COUNTRY_KEY) || 'US'
    } catch {
        return 'US'
    }
}

const useStartOnboarding = () => {
    const onboard = useConnectOnboard()
    const start = (code: string) => {
        try {
            window.sessionStorage.setItem(COUNTRY_KEY, code)
        } catch {
            // ignore
        }
        onboard.mutate(code, { onError: (error) => notify(errorMessage(error), 'error') })
    }
    return { start, isPending: onboard.isPending }
}

/**
 * Handles Stripe's return to /affiliate?connect=return|refresh once `me` has loaded, whatever the tab shows
 * (including the terms gate). The param is only a hint: status always comes from GET @me. A refresh (expired
 * onboarding link) goes straight back to Stripe only when onboarding is actually possible right now.
 */
export const useConnectReturn = (me: AffiliateMe | null | undefined) => {
    const location = useLocation()
    const history = useHistory()
    const queryClient = useQueryClient()
    const { start } = useStartOnboarding()
    const handled = useRef(false)

    useEffect(() => {
        if (me === undefined || handled.current) return
        const params = new URLSearchParams(location.search)
        const connect = params.get('connect')
        if (!connect) return
        handled.current = true

        params.delete('connect')
        const search = params.toString()
        history.replace({ ...location, search: search ? `?${search}` : '' })

        const canOnboard = !!me && ['active', 'paused'].includes(me.status) && !me.terms?.needsAcceptance
        if (connect === 'refresh' && canOnboard) {
            start(savedCountry())
        } else {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateMe] })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [me])
}

/** Payout setup state from `connect`. */
const ConnectBanner = ({ me }: { me: AffiliateMe }) => {
    const onboard = useStartOnboarding()
    const [country, setCountry] = useState(savedCountry)

    const startOnboarding = () => onboard.start(country)

    const connect = me.connect
    if (!connect) return null

    const banners = []

    if (connect.status === 'not_started' || connect.status === 'incomplete') {
        banners.push(
            <Banner key="setup" $tone="info">
                <p>
                    {connect.status === 'not_started'
                        ? 'Set up payouts with Stripe so you can withdraw your earnings.'
                        : "You haven't finished setting up payouts with Stripe."}
                </p>
                <Row>
                    {connect.status === 'not_started' && (
                        <Select
                            aria-label="Country of your bank account"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            style={{ width: 'auto' }}
                        >
                            {COUNTRIES.map(([code, name]) => (
                                <option key={code} value={code}>
                                    {name}
                                </option>
                            ))}
                        </Select>
                    )}
                    <ActionButton disabled={onboard.isPending} onClick={startOnboarding}>
                        {connect.status === 'not_started' ? 'Set up payouts' : 'Continue setup'}
                    </ActionButton>
                </Row>
            </Banner>
        )
    } else if (connect.status === 'restricted') {
        banners.push(
            <Banner key="restricted" $tone="error">
                <p>Stripe has paused payouts on your account. Update your details with Stripe to start them again.</p>
                <ActionButton disabled={onboard.isPending} onClick={startOnboarding}>
                    Update details
                </ActionButton>
            </Banner>
        )
    } else if (connect.requirementsDue.length > 0) {
        banners.push(
            <Banner key="due" $tone="warn">
                <p>Stripe needs more information from you. Payouts still work for now.</p>
                <SecondaryButton disabled={onboard.isPending} onClick={startOnboarding}>
                    Provide information
                </SecondaryButton>
            </Banner>
        )
    }

    if (connect.lastBankPayout && connect.lastBankPayout.status === 'failed') {
        banners.push(
            <Banner key="bank" $tone="error">
                <p>
                    Stripe couldn't deposit {formatCents(connect.lastBankPayout.amountCents)} to your bank
                    {connect.lastBankPayout.failureMessage ? `: ${connect.lastBankPayout.failureMessage}` : '.'} Update
                    your bank details in your Stripe dashboard.
                </p>
            </Banner>
        )
    }

    return <>{banners}</>
}

export default ConnectBanner
